import { Eye } from 'lucide-react';
import { useCanWrite } from '../lib/useCanWrite';
import { useWorkspace } from '../workspace/WorkspaceProvider';
import { useClientFrame } from '../advisor/useClientFrame';

// Declared locally rather than imported from Layout: Layout renders this component,
// and importing back would close an import cycle (same reason AccountMenu redeclares it).
const ICON_STROKE = 1.75;

/**
 * Top-bar cue for a read-only (Viewer) membership, sitting beside {@link EnvPill}.
 *
 * Without it, a viewer meets a portal that simply has no Add or Edit anywhere and has
 * to guess whether that's their account or a broken page. One quiet, permanent label
 * answers that. Renders nothing for anyone who can write.
 *
 * Waits for `meLoaded` as well as `!canWrite`, which is a stricter test than the one
 * guarding the buttons. `useCanWrite()` withholds *controls* while /me is in flight —
 * fine, nothing is asserted by an absent button. This pill would instead assert a
 * false account state ("A workspace admin can give you edit access") at every user,
 * admins included, for the width of that fetch. Silence is the honest default here.
 *
 * Suppressed entirely while `inClientFrame` (steward-app#1401): the client frame's own
 * band already says "read-only" in the honest, advisor-specific words this pill's fixed
 * copy cannot ("A workspace admin can give you edit access" is meaningless advice to an
 * advisor looking at someone else's family). Deliberately `inClientFrame`, not the
 * broader `inClientWorkspace` (`useClientFrame`'s header): a firm's own staff member
 * standing in their OWN family workspace is an ordinary member of it, and this pill must
 * still speak for them exactly as it would for anyone else in that seat. This pill stays
 * for the case it was built for — a read-only MEMBER of the active workspace, family or
 * firm.
 */
export function ViewOnlyPill() {
  const { meLoaded } = useWorkspace();
  const canWrite = useCanWrite();
  const { inClientFrame } = useClientFrame();
  if (inClientFrame) return null;
  if (!meLoaded || canWrite) return null;
  return (
    <span
      title="You can see everything in this workspace, but not make changes. A workspace admin can give you edit access."
      className="shrink-0 inline-flex items-center gap-1 rounded-token bg-navy/[0.06] px-1.5 py-0.5 font-sans text-[10px] font-semibold uppercase tracking-[0.16em] text-slate"
    >
      <Eye size={11} strokeWidth={ICON_STROKE} aria-hidden />
      View only
    </span>
  );
}
